import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";
import { useToast } from "@/components/ui/use-toast.ts";
import { AxiosError } from "axios";

export interface AvaChat {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface AvaMessage {
  id: string;
  role: "user" | "assistant";
  content:
    | string
    | Array<{
        type: string;
        text?: string;
        content?: string;
        toolCallId?: string;
        _toolCallId?: string;
        toolName?: string;
        args?: any;
        result?: any;
      }>;
  toolCalls?: any[];
  metadata?: Record<string, any>;
  createdAt: string;
}

export function useAvaChats() {
  return useQuery({
    queryKey: ["ava-chats"],
    queryFn: async (): Promise<AvaChat[]> => {
      const response = await fetchApi<{ data: AvaChat[] }>({
        url: "/v2/ava/chats",
        method: "GET",
      });

      if (!response.data?.data) throw new Error("No chats found");

      return response.data.data;
    },
  });
}

export function useAvaChat(chatId: string | null) {
  return useQuery({
    queryKey: ["ava-chat", chatId],
    queryFn: async (): Promise<AvaChat> => {
      if (!chatId) throw new Error("Chat ID is required");

      const response = await fetchApi<{ data: AvaChat }>({
        url: `/v2/ava/chats/${chatId}`,
        method: "GET",
      });

      if (!response.data?.data) throw new Error("No chat found");

      return response.data.data;
    },
    enabled: !!chatId,
  });
}

export function useAvaChatMessages(chatId: string | null) {
  return useQuery({
    queryKey: ["ava-chat-messages", chatId],
    queryFn: async (): Promise<AvaMessage[]> => {
      if (!chatId) throw new Error("Chat ID is required");

      const response = await fetchApi<{ data: AvaMessage[] }>({
        url: `/v2/ava/chats/${chatId}/messages`,
        method: "GET",
      });

      if (!response.data?.data) throw new Error("No messages found");

      return response.data.data;
    },
    enabled: !!chatId,
  });
}

export function useCreateAvaChat() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (title?: string): Promise<AvaChat> => {
      const response = await fetchApi<{ data: AvaChat }>({
        url: "/v2/ava/chats",
        method: "POST",
        data: { title },
      });

      if (!response.data?.data) throw new Error("Failed to create chat");

      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ava-chats"] });
      toast({
        title: "Chat created",
        description: "New AVA chat created successfully",
      });
    },
    onError: (error) => {
      let description: string | undefined = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Failed to create chat",
        description,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateAvaChatTitle() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      chatId,
      title,
    }: {
      chatId: string;
      title: string;
    }) => {
      await fetchApi({
        url: `/v2/ava/chats/${chatId}/title`,
        method: "PUT",
        data: { title },
      });
    },
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: ["ava-chats"] });
      queryClient.invalidateQueries({ queryKey: ["ava-chat", chatId] });
      toast({
        title: "Chat updated",
        description: "Chat title updated successfully",
      });
    },
    onError: (error) => {
      let description: string | undefined = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Failed to update chat",
        description,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteAvaChat() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (chatId: string) => {
      await fetchApi({
        url: `/v2/ava/chats/${chatId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ava-chats"] });
      toast({
        title: "Chat deleted",
        description: "Chat deleted successfully",
      });
    },
    onError: (error) => {
      let description: string | undefined = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Failed to delete chat",
        description,
        variant: "destructive",
      });
    },
  });
}
